import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon } from 'lucide-react';
import type { SlideProps } from './SlideShell';
function formatEuro(value: number) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(value);
}
export function SlideCostDisruptor({}: SlideProps) {
  const [brands, setBrands] = useState(5);
  const [campaignsPerBrand, setCampaignsPerBrand] = useState(4);
  const [countries, setCountries] = useState(25);
  const costPerLocalizedCampaign = useMemo(() => {
    const masterBase = 2900;
    const masterFloor = 1900;
    const followUpMaster = 1000;
    const localizationBase = 999;
    const insightsBase = 99;
    const campaignDuration = 3;
    const totalCampaigns = brands * campaignsPerBrand;
    const masterProgress = Math.min(1, Math.max(0, (totalCampaigns - 1) / 99));
    const masterCost = masterBase - (masterBase - masterFloor) * masterProgress;
    const countryDiscount = Math.min(0.5, Math.max(0, (countries - 2) / 48 * 0.5));
    const localizationCost = localizationBase * (1 - countryDiscount);
    const insightsCost = insightsBase * (1 - countryDiscount);
    const perCampaignCountryCost = localizationCost + insightsCost * campaignDuration;
    const firstCampaigns = brands;
    const followUpCampaigns = Math.max(0, totalCampaigns - brands);
    const annualCost = firstCampaigns * (masterCost + perCampaignCountryCost * countries) + followUpCampaigns * (followUpMaster + perCampaignCountryCost * countries);
    const deployments = totalCampaigns * countries;
    return deployments > 0 ? annualCost / deployments : 0;
  }, [brands, campaignsPerBrand, countries]);
  const sliders = [{
    label: 'Brands',
    value: brands,
    min: 1,
    max: 40,
    onChange: setBrands
  }, {
    label: 'Campaigns per brand / year',
    value: campaignsPerBrand,
    min: 2,
    max: 12,
    onChange: setCampaignsPerBrand
  }, {
    label: 'Countries',
    value: countries,
    min: 1,
    max: 100,
    onChange: setCountries
  }];
  return <main className="relative flex h-full w-full overflow-hidden bg-[var(--secondary)] text-white" data-id="element-209" data-mp="2uxtndjk18a1g">
      <div aria-hidden="true" className="absolute left-[56%] top-0 h-full w-px bg-white/10" data-id="element-210" data-mp="2zwwkmhtrtbyv" />

      <section className="relative z-10 flex w-[58%] flex-col justify-between px-[clamp(2.5rem,5vw,5.5rem)] py-[clamp(2.4rem,4.5vw,4.75rem)]" data-id="element-211" data-mp="34x2em4nijak4">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55
      }} data-id="element-212" data-mp="27absvg9duxus">
          <div className="flex items-center gap-3" data-id="element-213" data-mp="2h5wz5qlhikac">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--brand)] text-white shadow-lg" data-id="element-214" data-mp="2h53oiejenamx">
              <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-215" data-mp="1oisph8bg5hs8" />
            </span>
            <p className="text-[clamp(0.65rem,0.9vw,0.8rem)] font-bold uppercase tracking-[0.22em] text-white/70" data-id="element-216" data-mp="1th403l9kfwbi">
              The campaign cost disruptor
            </p>
          </div>

          <h2 className="mt-[clamp(1.5rem,3.5vw,3.5rem)] max-w-[52rem] font-extrabold tracking-[-0.045em] text-white" data-id="element-217" data-mp="6ko0y9634jmi">
            <span className="block max-w-[42rem] text-[clamp(1.9rem,3.4vw,3.6rem)] leading-[1.04]" data-id="element-218" data-mp="1l1nbymch1d6">
              A turn-key marketing campaign for
            </span>
            <span className="mb-6 mt-5 block text-[clamp(4.2rem,8vw,8rem)] leading-[0.85] text-[var(--brand)]" data-id="element-219" data-mp="3stysfkf1dzhz">
              € 1000?
            </span>
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.55
      }} className="my-auto max-w-[48rem]" data-id="element-220" data-mp="3j0rjjpl381d5">
          <p className="max-w-[46rem] text-[clamp(1.9rem,3.4vw,3.6rem)] font-extrabold leading-[1.04] tracking-[-0.04em] text-white" data-id="element-221" data-mp="352a04427tb11">
            So what happens when an agency is built around AI, not hours?
          </p>
          <p className="mt-5 max-w-[43rem] text-[clamp(0.85rem,1.15vw,1.05rem)] font-medium leading-relaxed text-white/60" data-id="element-222" data-mp="13zsefilwmlee">
            Content creation, local adaptation, channel activation and insights,
            scaled across the portfolio. Media costs excluded.
          </p>
        </motion.div>

        <p className="max-w-2xl text-[clamp(0.6rem,0.85vw,0.75rem)] font-bold uppercase tracking-[0.18em] text-white/35" data-id="element-223" data-mp="z8k3uj8u8tzn">
          Complete campaign delivery · Built to scale
        </p>
      </section>

      <motion.aside initial={{
      opacity: 0,
      x: 30
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.2,
      duration: 0.6
    }} className="relative z-10 flex w-[42%] items-center px-[clamp(1.5rem,3vw,3.5rem)] py-[clamp(2rem,4vw,4rem)]" aria-label="Campaign cost calculator" data-id="element-224" data-mp="q9oytahj5pxx">
        <div className="w-full rounded-[2rem] border border-white/15 bg-white/[0.06] p-[clamp(1.25rem,2.4vw,2.25rem)] shadow-2xl backdrop-blur-sm" data-id="element-225" data-mp="25pxjp7n6lwlx">
          <div className="mb-6" data-id="element-226" data-mp="3v69vabrzsybr">
            <p className="text-[clamp(0.6rem,0.85vw,0.75rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand-light)]" data-id="element-227" data-mp="3v7kvy2q55pyn">
              Break the agency model.
            </p>
            <h3 className="mt-2 text-[clamp(1.6rem,2.6vw,2.5rem)] font-extrabold leading-tight tracking-[-0.04em] text-white" data-id="element-228" data-mp="8r8cgv67byma">
              Move the sliders.
            </h3>
          </div>

          <motion.div key={Math.round(costPerLocalizedCampaign)} initial={{
          opacity: 0.65,
          y: 5
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25
        }} className="mb-6 rounded-2xl border border-white/15 bg-white/10 px-5 py-4" aria-live="polite" data-id="element-229" data-mp="hr3rxc6sifs4">
            <p className="text-[0.62rem] font-bold uppercase tracking-[0.15em] text-white/55" data-id="element-230" data-mp="ms17ri4buxqg">
              Modeled cost per executed, localized campaign*
            </p>
            <p className="mt-1 text-[clamp(2.1rem,3.4vw,3.25rem)] font-extrabold leading-none tracking-[-0.05em] text-white" data-id="element-231" data-mp="2oimjf3067jze">
              {formatEuro(costPerLocalizedCampaign)}
            </p>
          </motion.div>

          <div className="space-y-[clamp(1rem,1.8vw,1.6rem)]" data-id="element-232" data-mp="xf2o7q0uixye">
            {sliders.map(slider => <div key={slider.label} data-id="element-233" data-mp="11fd4pwm9l6ri">
                <div className="mb-3 flex items-center justify-between gap-5" data-id="element-234" data-mp="11ebj4y7mh1vj">
                  <label htmlFor={`cost-${slider.label}`} className="text-sm font-semibold text-white/80" data-id="element-235" data-mp="16fiq4v1xxx15">
                    {slider.label}
                  </label>
                  <output className="min-w-[3.5rem] rounded-xl border border-white/15 bg-white/10 px-3 py-1.5 text-center text-lg font-bold text-white" data-id="element-236" data-mp="1gcdlj52x3hb7">
                    {slider.value}
                  </output>
                </div>
                <input id={`cost-${slider.label}`} type="range" min={slider.min} max={slider.max} value={slider.value} onChange={event => slider.onChange(Number(event.target.value))} className="deck-slider w-full cursor-pointer" aria-label={slider.label} data-id="element-237" data-mp="1p4pdivdojcta" />
                <div className="mt-2 flex justify-between text-[0.65rem] font-semibold text-white/40" data-id="element-238" data-mp="297uysi1vclwg">
                  <span data-id="element-239" data-mp="247pejmxxi1oe">{slider.min}</span>
                  <span data-id="element-240" data-mp="246xikb0f61xl">{slider.max}</span>
                </div>
              </div>)}
          </div>

          <p className="mt-6 text-[0.7rem] font-medium leading-relaxed text-white/45" data-id="element-241" data-mp="1z9fjdcc7f12s">
            *Media spend excluded. The more markets and campaigns activated, the
            more efficiently each country campaign can be executed.
          </p>
        </div>
      </motion.aside>
    </main>;
}